import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider, MutationCache } from '@tanstack/react-query';
import { GoogleOAuthProvider } from '@react-oauth/google';
import * as Sentry from '@sentry/react';
import { trpc, trpcClient } from './lib/trpc';
import { ToastProvider } from './components/Toast';
import ErrorBoundary from './components/ErrorBoundary';
import App from './App';
import './globals.css';
import { initCapacitor } from './lib/capacitor';

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID ?? '';

// Initialize Capacitor plugins (no-op on web)
initCapacitor();

// --- Sentry init (no-op when DSN is absent) ---
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.MODE,
    tracesSampleRate: import.meta.env.PROD ? 0.2 : 1.0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: import.meta.env.PROD ? 1.0 : 0,
  });
}

// Toast ref so QueryClient (created outside React) can trigger toasts
let _showToast: ((msg: string, type?: 'error' | 'success' | 'info') => void) | null = null;
export const setGlobalToast = (fn: typeof _showToast) => { _showToast = fn; };

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60, // 1 minute
      retry: 1,
    },
  },
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      // Redirect on UNAUTHORIZED
      if ('data' in error && (error as any).data?.code === 'UNAUTHORIZED') {
        window.location.href = '/auth';
        return;
      }
      // Skip toast if the mutation has its own onError (local handling)
      if (mutation.options.onError) return;
      // Show global toast for unhandled mutation errors
      const message = error instanceof Error ? error.message : 'Something went wrong';
      _showToast?.(message, 'error');
    },
  }),
});

// Conditionally wrap with GoogleOAuthProvider — the SDK throws if clientId is empty
const AppTree = (
  <trpc.Provider client={trpcClient} queryClient={queryClient}>
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </ToastProvider>
    </QueryClientProvider>
  </trpc.Provider>
);

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ErrorBoundary>
      {GOOGLE_CLIENT_ID
        ? <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>{AppTree}</GoogleOAuthProvider>
        : AppTree}
    </ErrorBoundary>
  </React.StrictMode>
);
